import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Copy, Download, Eye, Plus, Trash2, Info } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface RobotRule {
  userAgent: string;
  allow: string[];
  disallow: string[];
}

interface RobotsConfig {
  rules: RobotRule[];
  sitemap: string[];
  crawlDelay: string;
  host: string;
}

const RobotsTxtGenerator: React.FC = () => {
  const [config, setConfig] = useState<RobotsConfig>({
    rules: [
      {
        userAgent: '*',
        allow: [],
        disallow: []
      }
    ],
    sitemap: [],
    crawlDelay: '',
    host: ''
  });

  const [generatedRobots, setGeneratedRobots] = useState('');
  const [presetType, setPresetType] = useState('');

  useSEO({
    title: 'Robots.txt Generator - Create SEO Robots.txt File',
    description: 'Generate robots.txt files to control search engine crawling. Create custom rules for different user agents, set crawl delays, and specify sitemaps.',
    keywords: 'robots.txt generator, robots txt, SEO robots file, search engine crawling, web crawler rules',
    canonical: 'https://snap-tools.online/seo/robots-txt-generator'
  });

  // Add new rule
  const addRule = () => {
    setConfig(prev => ({
      ...prev,
      rules: [...prev.rules, { userAgent: '*', allow: [], disallow: [] }]
    }));
  };

  // Remove rule
  const removeRule = (index: number) => {
    if (config.rules.length > 1) {
      setConfig(prev => ({
        ...prev,
        rules: prev.rules.filter((_, i) => i !== index)
      }));
    }
  };

  // Update rule
  const updateRule = (index: number, field: keyof RobotRule, value: string) => {
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map((rule, i) => 
        i === index ? { ...rule, [field]: value } : rule
      )
    }));
  };

  // Add allow/disallow path
  const addPath = (ruleIndex: number, type: 'allow' | 'disallow', path: string) => {
    if (!path.trim()) return;
    
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map((rule, i) => 
        i === ruleIndex 
          ? { ...rule, [type]: [...rule[type], path.trim()] }
          : rule
      )
    }));
  };

  // Remove allow/disallow path
  const removePath = (ruleIndex: number, type: 'allow' | 'disallow', pathIndex: number) => {
    setConfig(prev => ({
      ...prev,
      rules: prev.rules.map((rule, i) => 
        i === ruleIndex 
          ? { ...rule, [type]: rule[type].filter((_, pi) => pi !== pathIndex) }
          : rule
      )
    }));
  };

  // Add sitemap
  const addSitemap = (url: string) => {
    if (!url.trim()) return;
    
    setConfig(prev => ({
      ...prev,
      sitemap: [...prev.sitemap, url.trim()]
    }));
  };

  // Remove sitemap
  const removeSitemap = (index: number) => {
    setConfig(prev => ({
      ...prev,
      sitemap: prev.sitemap.filter((_, i) => i !== index)
    }));
  };

  // Load preset configurations
  const loadPreset = (type: string) => {
    const presets: { [key: string]: RobotsConfig } = {
      'allow-all': {
        rules: [{ userAgent: '*', allow: ['/'], disallow: [] }],
        sitemap: ['https://example.com/sitemap.xml'],
        crawlDelay: '',
        host: ''
      },
      'block-all': {
        rules: [{ userAgent: '*', allow: [], disallow: ['/'] }],
        sitemap: [],
        crawlDelay: '',
        host: ''
      },
      'wordpress': {
        rules: [
          {
            userAgent: '*',
            allow: [],
            disallow: ['/wp-admin/', '/wp-includes/', '/wp-content/plugins/', '/wp-content/themes/']
          }
        ],
        sitemap: ['https://example.com/sitemap.xml', 'https://example.com/sitemap_index.xml'],
        crawlDelay: '',
        host: ''
      },
      'ecommerce': {
        rules: [
          {
            userAgent: '*',
            allow: ['/products/', '/categories/'],
            disallow: ['/admin/', '/cart/', '/checkout/', '/account/', '/search/']
          }
        ],
        sitemap: ['https://example.com/sitemap.xml', 'https://example.com/products-sitemap.xml'],
        crawlDelay: '1',
        host: ''
      },
      'blog': {
        rules: [
          {
            userAgent: '*',
            allow: ['/'],
            disallow: ['/admin/', '/private/', '/drafts/']
          }
        ],
        sitemap: ['https://example.com/sitemap.xml', 'https://example.com/posts-sitemap.xml'],
        crawlDelay: '',
        host: ''
      }
    };

    if (presets[type]) {
      setConfig(presets[type]);
      setPresetType(type);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} preset loaded`);
    }
  };

  // Generate robots.txt content
  const generateRobots = () => {
    let robotsContent = '';

    // Add rules for each user agent
    config.rules.forEach(rule => {
      robotsContent += `User-agent: ${rule.userAgent}\n`;
      
      // Add disallow rules
      rule.disallow.forEach(path => {
        robotsContent += `Disallow: ${path}\n`;
      });
      
      // Add allow rules
      rule.allow.forEach(path => {
        robotsContent += `Allow: ${path}\n`;
      });
      
      // Add crawl delay if specified
      if (config.crawlDelay && rule.userAgent === '*') {
        robotsContent += `Crawl-delay: ${config.crawlDelay}\n`;
      }
      
      robotsContent += '\n';
    });

    // Add host if specified
    if (config.host) {
      robotsContent += `Host: ${config.host}\n\n`;
    }

    // Add sitemaps
    config.sitemap.forEach(sitemap => {
      robotsContent += `Sitemap: ${sitemap}\n`;
    });

    setGeneratedRobots(robotsContent.trim());
    toast.success('Robots.txt generated successfully');
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    if (!generatedRobots) {
      toast.error('No robots.txt content to copy. Please generate it first.');
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedRobots);
      toast.success('Robots.txt copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy robots.txt');
    }
  };

  // Download robots.txt file
  const downloadRobots = () => {
    if (!generatedRobots) {
      toast.error('No robots.txt content to download. Please generate it first.');
      return;
    }

    const blob = new Blob([generatedRobots], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'robots.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Robots.txt downloaded successfully');
  };

  // Common user agents
  const commonUserAgents = [
    '*',
    'Googlebot',
    'Bingbot',
    'Slurp', // Yahoo
    'DuckDuckBot',
    'Baiduspider',
    'YandexBot',
    'facebookexternalhit',
    'Twitterbot',
    'LinkedInBot'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Robots.txt Generator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Create and customize robots.txt files to control how search engines crawl your website. Set rules for different user agents, specify crawl delays, and include sitemap locations.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Preset Templates */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Start Templates</h3>
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { key: 'allow-all', label: 'Allow All', desc: 'Allow all crawlers' },
                { key: 'block-all', label: 'Block All', desc: 'Block all crawlers' },
                { key: 'wordpress', label: 'WordPress', desc: 'WordPress site rules' },
                { key: 'ecommerce', label: 'E-commerce', desc: 'Online store rules' },
                { key: 'blog', label: 'Blog', desc: 'Blog/news site rules' }
              ].map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => loadPreset(preset.key)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    presetType === preset.key
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-sm">{preset.label}</div>
                  <div className="text-xs text-gray-600 mt-1">{preset.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Configuration Form */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Crawler Rules</h3>
                  <button
                    onClick={addRule}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Rule
                  </button>
                </div>

                {config.rules.map((rule, ruleIndex) => (
                  <motion.div
                    key={ruleIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-50 rounded-lg p-4 mb-4"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          User Agent
                        </label>
                        <select
                          value={rule.userAgent}
                          onChange={(e) => updateRule(ruleIndex, 'userAgent', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          {commonUserAgents.map(agent => (
                            <option key={agent} value={agent}>{agent}</option>
                          ))}
                        </select>
                      </div>
                      {config.rules.length > 1 && (
                        <button
                          onClick={() => removeRule(ruleIndex)}
                          className="text-red-600 hover:text-red-700 mt-6"
                          title="Remove rule"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>

                    {/* Disallow Paths */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Disallow Paths
                      </label>
                      <div className="space-y-2">
                        {rule.disallow.map((path, pathIndex) => (
                          <div key={pathIndex} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={path}
                              onChange={(e) => {
                                const newDisallow = [...rule.disallow];
                                newDisallow[pathIndex] = e.target.value;
                                updateRule(ruleIndex, 'disallow', newDisallow as any);
                              }}
                              className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                            <button
                              onClick={() => removePath(ruleIndex, 'disallow', pathIndex)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="/private/"
                            className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                addPath(ruleIndex, 'disallow', e.currentTarget.value);
                                e.currentTarget.value = '';
                              }
                            }}
                          />
                          <button
                            onClick={(e) => {
                              const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                              addPath(ruleIndex, 'disallow', input.value);
                              input.value = '';
                            }}
                            className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Allow Paths */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Allow Paths
                      </label>
                      <div className="space-y-2">
                        {rule.allow.map((path, pathIndex) => (
                          <div key={pathIndex} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={path}
                              onChange={(e) => {
                                const newAllow = [...rule.allow];
                                newAllow[pathIndex] = e.target.value;
                                updateRule(ruleIndex, 'allow', newAllow as any);
                              }}
                              className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                            <button
                              onClick={() => removePath(ruleIndex, 'allow', pathIndex)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="/public/"
                            className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                addPath(ruleIndex, 'allow', e.currentTarget.value);
                                e.currentTarget.value = '';
                              }
                            }}
                          />
                          <button
                            onClick={(e) => {
                              const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                              addPath(ruleIndex, 'allow', input.value);
                              input.value = '';
                            }}
                            className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Global Settings */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Global Settings</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Crawl Delay (seconds)
                    </label>
                    <input
                      type="number"
                      value={config.crawlDelay}
                      onChange={(e) => setConfig(prev => ({ ...prev, crawlDelay: e.target.value }))}
                      placeholder="1"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Host (optional)
                    </label>
                    <input
                      type="url"
                      value={config.host}
                      onChange={(e) => setConfig(prev => ({ ...prev, host: e.target.value }))}
                      placeholder="https://example.com"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Sitemaps */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sitemaps
                    </label>
                    <div className="space-y-2">
                      {config.sitemap.map((sitemap, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="url"
                            value={sitemap}
                            onChange={(e) => {
                              const newSitemaps = [...config.sitemap];
                              newSitemaps[index] = e.target.value;
                              setConfig(prev => ({ ...prev, sitemap: newSitemaps }));
                            }}
                            className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                          <button
                            onClick={() => removeSitemap(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          type="url"
                          placeholder="https://example.com/sitemap.xml"
                          className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addSitemap(e.currentTarget.value);
                              e.currentTarget.value = '';
                            }
                          }}
                        />
                        <button
                          onClick={(e) => {
                            const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                            addSitemap(input.value);
                            input.value = '';
                          }}
                          className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={generateRobots}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Generate Robots.txt
              </button>
            </div>

            {/* Generated Output */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Generated Robots.txt</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={copyToClipboard}
                      disabled={!generatedRobots}
                      className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                    <button
                      onClick={downloadRobots}
                      disabled={!generatedRobots}
                      className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg border p-4 h-96 overflow-y-auto">
                  {generatedRobots ? (
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                      {generatedRobots}
                    </pre>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Configure your rules and click "Generate Robots.txt" to see the output</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Live Preview */}
              {generatedRobots && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-blue-50 rounded-lg p-6"
                >
                  <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    File Preview
                  </h4>
                  <div className="text-sm text-blue-800 space-y-2">
                    <p><strong>File Location:</strong> /robots.txt (root of your domain)</p>
                    <p><strong>File Size:</strong> {new Blob([generatedRobots]).size} bytes</p>
                    <p><strong>Rules Count:</strong> {config.rules.length}</p>
                    <p><strong>Sitemaps:</strong> {config.sitemap.length}</p>
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Robots.txt Best Practices</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>File Location:</strong> Must be at domain root (/robots.txt)</li>
                <li>• <strong>Case Sensitive:</strong> Use lowercase for file name</li>
                <li>• <strong>UTF-8 Encoding:</strong> Save file with UTF-8 encoding</li>
                <li>• <strong>Specific Rules:</strong> More specific rules override general ones</li>
                <li>• <strong>Test Regularly:</strong> Use Google Search Console to test</li>
                <li>• <strong>Don't Hide Sensitive Data:</strong> Robots.txt is publicly accessible</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5" />
                Important Notes
              </h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• <strong>Not Legally Binding:</strong> Crawlers can choose to ignore robots.txt</li>
                <li>• <strong>Public File:</strong> Anyone can view your robots.txt file</li>
                <li>• <strong>SEO Impact:</strong> Blocking important pages can hurt rankings</li>
                <li>• <strong>Crawl Budget:</strong> Use to optimize crawler resource usage</li>
                <li>• <strong>Regular Updates:</strong> Update when site structure changes</li>
                <li>• <strong>Backup:</strong> Keep backups before making changes</li>
              </ul>
            </div>
          </div>

          {/* Common Patterns */}
          <div className="mt-6 bg-green-50 rounded-lg p-6">
            <h4 className="font-semibold text-green-900 mb-3">Common Robots.txt Patterns</h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <h5 className="font-medium text-green-800 mb-2">WordPress Sites:</h5>
                <code className="block bg-white p-2 rounded text-xs">
                  Disallow: /wp-admin/<br/>
                  Disallow: /wp-includes/<br/>
                  Disallow: /wp-content/plugins/<br/>
                  Allow: /wp-content/uploads/
                </code>
              </div>
              <div>
                <h5 className="font-medium text-green-800 mb-2">E-commerce Sites:</h5>
                <code className="block bg-white p-2 rounded text-xs">
                  Disallow: /cart/<br/>
                  Disallow: /checkout/<br/>
                  Disallow: /account/<br/>
                  Allow: /products/
                </code>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RobotsTxtGenerator;
